<script setup lang="ts">
import { useQuery } from '@tanstack/vue-query';
import { ref, computed } from 'vue';
import { patientTasksApi, type PatientTask, type TimeType } from '@/api/patient-tasks';
import { taskDefinitionsApi } from '@/api/task-definitions';
import { usePermissions } from '@/composables/usePermissions';
import { useResponsiveTable } from '@/composables/useResponsiveTable';
import PatientTaskFormComponent from './patient-task-form.vue';
import PatientTaskDiscardForm from './patient-task-discard-form.vue';
import PatientTaskUndiscardForm from './patient-task-undiscard-form.vue';

interface Props {
  patientId: number;
}

const props = defineProps<Props>();

const { hasPermission } = usePermissions();
const { tableProps } = useResponsiveTable({ desktopItemsPerPage: 10 });

const showForm = ref(false);
const selectedTask = ref<PatientTask | null>(null);
const scope = ref<'active' | 'discarded'>('active');
const hasActions = hasPermission.value('tasks', 'assign');

const {
  data: activePatientTasks,
  refetch: refetchActiveTasks,
  isPending: isLoadingActiveTasks,
} = useQuery({
  queryKey: ['patientTasks', 'active', props.patientId],
  queryFn: () => patientTasksApi.index(props.patientId, 'active'),
  initialData: [],
});

const {
  data: discardedPatientTasks,
  refetch: refetchDiscardedTasks,
  isPending: isLoadingDiscardedTasks,
} = useQuery({
  queryKey: ['patientTasks', 'discarded', props.patientId],
  queryFn: () => patientTasksApi.index(props.patientId, 'discarded'),
  initialData: [],
});

const { data: taskDefinitions } = useQuery({
  queryKey: ['taskDefinitions', 'active'],
  queryFn: () => taskDefinitionsApi.index(undefined, 'active'),
  initialData: [],
});

const patientTasks = computed(() =>
  (scope.value === 'active' ? activePatientTasks.value : discardedPatientTasks.value),
);

const headers = computed(() => {
  const baseHeaders = [
    { title: 'Categoría', key: 'taskCategoryName' },
    { title: 'Tarea', key: 'taskDefinitionName' },
    { title: 'Descripción', key: 'taskDefinitionDescription' },
    { title: 'Hora Programada', key: 'humanScheduledTime' },
    { title: 'Observaciones', key: 'observations' },
    { title: 'Asignado por', key: 'assignedByEmail' },
  ];

  if (scope.value === 'discarded') {
    baseHeaders.push({ title: 'Fecha Archivada', key: 'humanDiscardedAt' });
  } else {
    baseHeaders.push({ title: 'Fecha de Creación', key: 'humanCreatedAt' });
  }

  if (hasActions) {
    baseHeaders.push({ title: 'Acciones', key: 'actions' });
  }

  return baseHeaders;
});

function openCreateForm() {
  selectedTask.value = null;
  showForm.value = true;
}

function openEditForm(task: PatientTask) {
  selectedTask.value = task;
  showForm.value = true;
}

function handleFormSuccess() {
  showForm.value = false;
  selectedTask.value = null;
  refetchActiveTasks();
  refetchDiscardedTasks();
}

function handleFormClose() {
  showForm.value = false;
  selectedTask.value = null;
}

function handleDiscardAndUndiscardSuccess() {
  refetchActiveTasks();
  refetchDiscardedTasks();
}

const isLoading = computed(() =>
  isLoadingActiveTasks.value ||
  isLoadingDiscardedTasks.value,
);

const timeTypeColors: Record<TimeType, string> = {
  specific: 'primary',
  am: 'orange-lighten-1',
  pm: 'blue-grey-darken-1',
};
</script>
<template>
  <div>
    <div class="d-flex justify-space-between align-center mb-4">
      <span class="text-h6">Tareas Asignadas</span>
      <v-btn
        v-if="scope === 'active' && hasPermission('tasks', 'assign')"
        color="primary"
        :disabled="isLoading"
        @click="openCreateForm"
      >
        <v-icon start>
          mdi-plus
        </v-icon>
        Asignar Tarea
      </v-btn>
    </div>

    <v-toolbar
      color="white"
      class="ml-2"
      flat
    >
      <v-tabs
        v-model="scope"
        color="primary"
      >
        <v-tab
          value="active"
          class="rounded-pill text-body-1"
        >
          Activas
        </v-tab>
        <v-tab
          value="discarded"
          class="rounded-pill text-body-1"
        >
          Archivadas
        </v-tab>
      </v-tabs>
    </v-toolbar>
    <v-data-table
      v-bind="tableProps"
      :headers="headers"
      :header-props="{ class: 'font-weight-bold' }"
      :items="patientTasks"
      :loading="isLoading"
      class="elevation-0"
      density="comfortable"
    >
      <template #[`item.taskCategoryName`]="{ item }">
        <v-chip
          color="primary"
          size="small"
          variant="outlined"
        >
          {{ item.taskCategoryName }}
        </v-chip>
      </template>
      <template #[`item.humanScheduledTime`]="{ item }">
        <v-chip
          :color="timeTypeColors[item.timeType as TimeType]"
          variant="tonal"
          size="small"
        >
          <v-icon
            v-if="item.timeType === 'am'"
            icon="mdi-weather-sunny"
            size="small"
            class="mr-1"
          />
          <v-icon
            v-else-if="item.timeType === 'pm'"
            icon="mdi-weather-night"
            size="small"
            class="mr-1"
          />
          {{ item.humanScheduledTime }}
        </v-chip>
      </template>
      <template #[`item.observations`]="{ item }">
        <div
          v-if="item.observations"
          class="text-body-2"
        >
          {{ item.observations }}
        </div>
        <div
          v-else
          class="text-caption text-medium-emphasis"
        >
          Sin observaciones
        </div>
      </template>
      <template #[`item.actions`]="{ item }">
        <div
          v-if="scope === 'active' && hasActions"
          class="d-flex"
        >
          <v-btn
            v-if="hasPermission('tasks', 'assign')"
            icon="mdi-pencil"
            variant="text"
            color="primary"
            size="small"
            :disabled="isLoading"
            @click="openEditForm(item)"
          >
            <v-icon>mdi-pencil</v-icon>
            <v-tooltip
              activator="parent"
              location="top"
            >
              Editar Tarea
            </v-tooltip>
          </v-btn>
          <v-dialog
            max-width="600"
            scrollable
          >
            <template #activator="{ props: activatorProps }">
              <v-btn
                v-if="hasPermission('tasks', 'assign')"
                v-bind="activatorProps"
                icon="mdi-delete"
                variant="text"
                color="warning"
                size="small"
                :disabled="isLoading"
              >
                <v-icon>mdi-delete</v-icon>
                <v-tooltip
                  activator="parent"
                  location="top"
                >
                  Archivar Tarea
                </v-tooltip>
              </v-btn>
            </template>
            <template #default="{ isActive }">
              <patient-task-discard-form
                :patient-task="item"
                @close="isActive.value = false"
                @success="handleDiscardAndUndiscardSuccess"
              />
            </template>
          </v-dialog>
        </div>
        <div
          v-else-if="scope === 'discarded' && hasActions"
          class="d-flex"
        >
          <v-dialog
            max-width="600"
            scrollable
          >
            <template #activator="{ props: activatorProps }">
              <v-btn
                v-if="hasPermission('tasks', 'assign')"
                v-bind="activatorProps"
                icon="mdi-package-up"
                variant="text"
                color="success"
                size="small"
                :disabled="isLoading"
              >
                <v-icon>mdi-package-up</v-icon>
                <v-tooltip
                  activator="parent"
                  location="top"
                >
                  Desarchivar Tarea
                </v-tooltip>
              </v-btn>
            </template>
            <template #default="{ isActive }">
              <patient-task-undiscard-form
                :patient-task="item"
                @close="isActive.value = false"
                @success="handleDiscardAndUndiscardSuccess"
              />
            </template>
          </v-dialog>
        </div>
      </template>
      <template #no-data>
        <div
          v-if="scope === 'active'"
          class="py-8 text-center"
        >
          <v-icon
            size="64"
            color="grey-lighten-2"
            class="mb-4"
          >
            mdi-clipboard-list-outline
          </v-icon>
          <div class="mb-2 text-h6 text-medium-emphasis">
            Sin tareas asignadas
          </div>
          <div class="mb-4 text-body-2 text-medium-emphasis">
            No hay tareas asignadas para este paciente
          </div>
          <v-btn
            v-if="hasPermission('tasks', 'assign')"
            color="primary"
            @click="openCreateForm"
          >
            <v-icon start>
              mdi-plus
            </v-icon>
            Asignar Primera Tarea
          </v-btn>
        </div>
        <div
          v-else
          class="py-8 text-center"
        >
          <p>
            No hay tareas archivadas para este paciente
          </p>
        </div>
      </template>
    </v-data-table>

    <v-dialog
      v-model="showForm"
      max-width="600"
      persistent
    >
      <patient-task-form-component
        :patient-id="patientId"
        :task-definitions="taskDefinitions"
        :patient-task="selectedTask"
        @success="handleFormSuccess"
        @close="handleFormClose"
      />
    </v-dialog>
  </div>
</template>
